import { useState } from "react";
import StarRating from "../../components/StarRating/StarRating";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { Link, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import "./Reviews.css";
import { useGetReviews } from "../../hooks/useGetReviews";
import SingleProductLoader from "../../components/SingleProductLoader/SingleProductLoader";

function Reviews() {
  const { slug } = useParams();
  const { user } = useSelector((state) => state.auth);
  const queryClient = useQueryClient();
  const product = queryClient.getQueryData(["product", slug, user?.id]);
  const [page, setPage] = useState(1);

  const { data: reviews, isLoading, error } = useGetReviews(product, page);

  if (!product) {
    return <section className="Reviews">Product not available...</section>;
  }

  if (isLoading) {
    return (
      <section className="Reviews">
        <div className="loading-container">
          <SingleProductLoader />
        </div>
      </section>
    );
  }

  if (error) {
    return <section className="Reviews">Error loading reviews</section>;
  }

  const totalReviews = product?.rating_count || 0;
  const reviewsPerPage = 10;
  const start = (page - 1) * reviewsPerPage + 1;
  const end = Math.min(page * reviewsPerPage, totalReviews);

  // Safe rating calculation
  const safeRating = product.average_rating
    ? parseFloat(product.average_rating).toFixed(1)
    : "0.0";

  console.log(product);

  return (
    <section className="Reviews">
      <div className="product-info">
        <img src={product.images?.[0]?.src} alt={product.name} />
        <div className="product-review-info">
          <h1 className="name">{product.name}</h1>
          <h1 className="rating">{`${safeRating}/5`}</h1>
          <div className="rating-count">
            <StarRating rating={product.average_rating || 0} />
            <p>{product.rating_count || 0} reviews</p>
          </div>
          <Link to={`/product/${product.slug?.toLowerCase() || ""}`}>
            View product details
          </Link>
        </div>
      </div>

      <div className="reviews-count">
        <p>
          <b>{`${start} - ${end} out of ${totalReviews} reviews`}</b>
        </p>
      </div>

      <div className="product-reviews">
        {reviews?.map((review) => {
          let timeAgo = "Unknown time";
          try {
            if (review.date_created) {
              timeAgo = formatDistanceToNow(parseISO(review.date_created));
            }
          } catch (error) {
            console.warn("Date parsing error:", error);
          }

          return (
            <div className="review-content" key={review.id}>
              <div className="reviewer">
                <StarRating rating={review.rating || 0} />
                <h3>{review.reviewer || "Anonymous"}</h3>
                <p>{timeAgo} ago</p>
              </div>
              <div className="review">
                <p dangerouslySetInnerHTML={{ __html: review.review || "" }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination controls */}
      <div className="pagination">
        <button
          onClick={() => setPage((old) => Math.max(old - 1, 1))}
          disabled={page === 1}
        >
          <ArrowLeft size={18} />
        </button>
        <button
          onClick={() => setPage((old) => old + 1)}
          disabled={end >= totalReviews}
        >
          <ArrowRight size={18} />
        </button>
      </div>
    </section>
  );
}

export default Reviews;
